/**
 * Getting a brand-new store from this browser onto the server.
 *
 * This is the one sequence in the app whose ORDER is load-bearing, because
 * three things each depend on the previous one having reached the server:
 *
 *   1. `registerDevice()` — creates the `devices` row. A slot is allocated by
 *      UPDATING that row, so without it allocation fails outright.
 *   2. `syncDatabase()`   — the pull returns an empty changeset (the store does
 *      not exist server-side yet) and the push then creates it. That push is
 *      the ONLY way a store ever reaches the server: there is no store-creation
 *      endpoint, by design — a till that could only open a store while online
 *      would be a worse till.
 *   3. `ensureDeviceSlot()` — now that the store row exists, the device can be
 *      given its slot (0–99). `useCreateInvoice` refuses to bill without one,
 *      because the slot is what makes offline invoice numbers collision-proof.
 *
 * Step 3 genuinely needs a SECOND round trip. The slot is allocated during a
 * pull, and the only pull in step 2 ran before the store existed. Leaving it to
 * the periodic sync looked fine and was not: `AppShell` calls `ensureDeviceSlot`
 * once on mount, which races the push in step 2 and usually loses, so the
 * merchant's first sale failed with "This device is not activated for billing
 * yet. Connect to the internet once to activate it" — while they were online,
 * with no way to make it true.
 *
 * Runs at module scope rather than inside the setup screen on purpose: setting
 * the active store re-renders the app into the till and unmounts that screen
 * mid-flight, so anything owned by the component would be abandoned half-done.
 * Problems are reported through `syncError`, which the shell already surfaces.
 */
import { useAppStore } from '@/store/useAppStore';
import { registerDevice } from '@/services/deviceIdentity';
import { ensureDeviceSlot } from '@/services/DeviceSlotService';
import { syncDatabase } from '@/sync';

/** Bounded retries: enough to outlast the shell's first failing sync, few
 *  enough that a genuinely offline till reaches its local-only state quickly. */
const SYNC_ATTEMPTS = 3;
const SLOT_ATTEMPTS = 3;
const RETRY_BASE_MS = 1200;

const pause = (ms: number): Promise<void> => new Promise((r) => setTimeout(r, ms));

export interface BootstrapResult {
  /** The store row reached the server. */
  pushed: boolean;
  /** A device slot was allocated — the till can bill. */
  slot: number | null;
  /** Operator-facing explanation when something above did not happen. */
  problem: string | null;
}

/**
 * Publishes a newly provisioned store and activates this browser for billing.
 *
 * Never throws: the store and its settings are already durable in IndexedDB, so
 * a failure here costs reach, not data. The merchant keeps a working local till
 * and the next ordinary sync retries the same steps.
 */
export async function bootstrapStoreOnServer(): Promise<BootstrapResult> {
  const setSyncError = useAppStore.getState().setSyncError;

  // 1 — Device row. Best-effort, but a failure here guarantees no slot, so it
  // is worth saying which step broke rather than reporting a generic sync error.
  let deviceRegistered = true;
  try {
    await registerDevice();
  } catch (err) {
    deviceRegistered = false;
    console.warn('[bootstrap] device registration failed', err);
  }

  // 2 — Publish the store. The pull inside this call is expected to come back
  // empty; the push is the part that matters.
  //
  // Never accept the FIRST failure here. syncDatabase() joins an in-flight
  // cycle rather than starting a new one, and there almost always is one:
  // provisionStore sets the active store, which mounts the shell, which syncs
  // at once — before step 1 above has finished registering this device. That
  // cycle fails, because the pull 503s for a device with no row yet. Joining
  // it means reporting failure for a store that is perfectly publishable now
  // that registration has completed, which is exactly what left a new
  // merchant unable to bill until they signed out and back in: the sign-in
  // path registers the device BEFORE activating the store, so its sync works.
  //
  // Retrying starts a genuinely new cycle, with the device in place.
  let pushed = false;
  for (let attempt = 0; attempt < SYNC_ATTEMPTS && !pushed; attempt++) {
    if (attempt > 0) await pause(RETRY_BASE_MS * attempt);
    try {
      pushed = await syncDatabase();
    } catch (err) {
      console.warn(`[bootstrap] sync attempt ${attempt + 1} failed`, err);
    }
  }

  if (!pushed) {
    const problem =
      'Your store was created on this device but has not reached the server yet. ' +
      'Billing stays available here; reconnect and sync to finish setting it up.';
    setSyncError(problem);
    return { pushed: false, slot: null, problem };
  }

  // 3 — The second round trip. The store exists now, so this allocates.
  //
  // Also retried: the push above returns as soon as the server accepts it, and
  // a slot is allocated against the store row that push created. A single
  // attempt occasionally lands in the gap.
  let slot: number | null = null;
  for (let attempt = 0; attempt < SLOT_ATTEMPTS && slot == null; attempt++) {
    if (attempt > 0) await pause(RETRY_BASE_MS * attempt);
    try {
      slot = await ensureDeviceSlot();
    } catch (err) {
      console.warn(`[bootstrap] slot attempt ${attempt + 1} failed`, err);
    }
  }

  if (slot == null) {
    const problem = deviceRegistered
      ? 'This browser could not claim a billing slot for the new store. Sync again to activate it.'
      : 'This browser could not be registered for the new store, so it cannot bill yet. Sync again to activate it.';
    setSyncError(problem);
    return { pushed: true, slot: null, problem };
  }

  setSyncError(null);
  return { pushed: true, slot, problem: null };
}
